import { ModuleRegistry } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import React, { useState, useEffect, useMemo,useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import axios from "axios";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-grid.css";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-theme-alpine.css";
import { getHeaders } from "../ApiHeaders/apiHeaders";
import { ColDef } from "ag-grid-community";
import { FaUser, FaBuilding, FaFileAlt, FaCalendarAlt, FaDownload } from 'react-icons/fa';
// @ts-ignore CSS is loaded by the application bundler.
import "../Admin/AgGridTheme.css";
import Dialog from "../Admin/FormDialog/Dialog";
import { useFileCenterContext } from "../../components/DataContext/FileCenterContext";
import { useAssetContext } from "../../components/DataContext/AssetContext";
import apiClient from "../../services/apiClient";
import DownloadButton from "./DownloadButton";
import UploadButton from "./UploadButton";
import UploadDialog from "./UploadDialog";
import { RotateCcw } from "lucide-react"; 
import DateFloatingFilter from "./DateFloatingFilter";
import DropdownFloatingFilter from "./DropdownFloatingFilter";
import LoadingStyle from "../Loading/Loading";
// import SortHeader from
import SortHeader from '../Admin/SortingHeader';
// ModuleRegistry.registerModules([ClientSideRowModelModule]);
ModuleRegistry.registerModules([AllCommunityModule]);


// ------------------ TYPES ------------------
interface FileDetails {
  assetId: string;
  fileName: string;
  fileType: string;
  createdDate: string;
  categoryName: string;
  lastModified?: string;
  folderName?: string;
  selected?: boolean;
}

interface TreeNode {
  name: string;
  isLeaf?: boolean;
  children?: TreeNode[];
  files?: FileDetails[];
}

const parseNumber = (s: string): number => {
  const n = Number(s);
  return Number.isNaN(n) ? 0 : n;
};

const extractTopFiles = (data: any): FileDetails[] => {
  const topFiles: FileDetails[] = [];

  Object.keys(data || {}).forEach((assetId) => {
    const assetData = data[assetId];
    if (!assetData) return;

    Object.keys(assetData).forEach((categoryName) => {
      const yearsObj = assetData[categoryName];
      if (!yearsObj) return;

      const years = Object.keys(yearsObj).sort((a, b) => parseNumber(b) - parseNumber(a));

      let found = false;
      for (const year of years) {
        if (found) break;
        const monthsObj = yearsObj[year] || {};
        const months = Object.keys(monthsObj).sort((a, b) => parseNumber(b) - parseNumber(a));

        for (const month of months) {
          if (found) break;
          const datesObj = monthsObj[month] || {};
          const dates = Object.keys(datesObj).sort((a, b) => parseNumber(b) - parseNumber(a));

          for (const date of dates) {
            const filesArray = datesObj[date];
            if (Array.isArray(filesArray) && filesArray.length > 0) {
              const firstFile = filesArray[0];
              topFiles.push({
                assetId,
                fileName: firstFile.file_name || firstFile.fileName,
                fileType: (firstFile.file_name || firstFile.fileName).split(".").pop() || "",
                createdDate: `${year}-${month.toString().padStart(2, "0")}-${date.toString().padStart(2, "0")}`,
                folderName: categoryName,
                lastModified: firstFile.last_modified || firstFile.lastModified,
                categoryName,
                selected: false,
              });
              found = true;
              break;
            }
          }
        }
      }
    });
  });

  return topFiles;
};


const transformToTree = (
  apiData: Record<string, any>,
  selectedAssetId: string | null
): TreeNode[] => {
  const tree: Record<string, any> = {};

  const assetIds = selectedAssetId
    ? [selectedAssetId]
    : Object.keys(apiData || {});

  assetIds.forEach((assetId) => {
    const categories = apiData[assetId];
    if (!categories) return;

    Object.entries(categories).forEach(([category, years]: any) => {
      Object.entries(years || {}).forEach(([year, months]: any) => {
        Object.entries(months || {}).forEach(([month, dates]: any) => {
          Object.entries(dates || {}).forEach(([date, files]: any) => {
            if (!Array.isArray(files) || files.length === 0) return;

            tree[category] ??= {};
            tree[category][year] ??= {};
            tree[category][year][month] ??= {};
            tree[category][year][month][date] = files.map((file: any) => ({
              assetId,
              fileName: file.file_name || file.fileName,
              fileType: (file.file_name || file.fileName).split(".").pop() || "",
              createdDate: `${year}-${month}-${date}`,
              lastModified: file.last_modified || file.lastModified,
              folderName: category,
              categoryName: category,
              selected: false,
            }));
          });
        });
      });
    });
  });

  const toNodes = (obj: any, level = 0): TreeNode[] =>
    Object.keys(obj)
      .sort((a, b) => parseNumber(b) - parseNumber(a))
      .map((key) => ({
        name: key,
        isLeaf: level === 3,
        children: level < 3 ? toNodes(obj[key], level + 1) : undefined,
        files: level === 3 ? obj[key] : undefined,
      }));

  return toNodes(tree);
};

const filterTree = (node: TreeNode, term: string): TreeNode | null => {
  const matches = node.name.toLowerCase().includes(term);
  if (matches) return node;

  if (node.children) {
    const filteredChildren = node.children
      .map((child) => filterTree(child, term))
      .filter(Boolean) as TreeNode[];

    if (filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }
  }

  return null;
};


// ------------------ TREE COMPONENT ------------------
interface CategoryTreeProps {
  treeData: TreeNode[];
  onDateSelect: (files: FileDetails[]) => void;
  apiData: any;
  selectedAssetId: string | null;
  onFolderSelect: (filteredData: any, context: string) => void;
}

const CategoryTree: React.FC<CategoryTreeProps> = ({
  treeData,
  onDateSelect,
  apiData,
  selectedAssetId,
  onFolderSelect,
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const renderTree = (nodes: TreeNode[], path: string[] = []) =>
    nodes.map((node) => {
      const currentPath = [...path, node.name];
      const key = currentPath.join("-");
      const isLeaf = node.isLeaf;
      const isOpen = expandedNodes[key];
      const isSelected = selectedKey === key;

      return (
        <div key={key} style={{ marginLeft: path.length * 12 }}>
          <div
            className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded-md transition font-poppins 
              ${isSelected ? "bg-violet-100 text-violet-700" : "text-gray-700 hover:bg-gray-100"}`}
              onClick={() => {
                setSelectedKey(key);
              
                if (isLeaf && node.files) {
                  onDateSelect(node.files);
                  const filteredData = { ...apiData };
                  filteredData.files = node.files;
                  onFolderSelect(filteredData, currentPath.join("/"));
                  return;
                }
              
                setExpandedNodes((prev) => ({ ...prev, [key]: !prev[key] }));
              
                const depth = currentPath.length;
                let filteredData = apiData;
              
                if (depth === 3) {
                  const [category, year, month] = currentPath;
                  let collectedFiles: any[] = [];
              
                  Object.entries(apiData).forEach(([assetId, assetData]: [string, any]) => {
                    if (selectedAssetId && assetId !== selectedAssetId) return;
                    const monthData = assetData?.[category]?.[year]?.[month];
                    if (!monthData) return;
              
                    Object.entries(monthData).forEach(([date, filesArray]: [string, any]) => {
                      if (Array.isArray(filesArray)) {
                        collectedFiles.push(
                          ...filesArray.map((file: any) => ({
                            assetId,
                            fileName: file.file_name || file.fileName,
                            fileType: (file.file_name || file.fileName).split(".").pop() || "",
                            createdDate: `${year}-${month}-${date}`,
                            folderName: category,
                            lastModified: file.last_modified || file.lastModified,
                            categoryName: category,
                            selected: false,
                          }))
                        );
                      }
                    });
                  });
              
                  onDateSelect(collectedFiles);
                  onFolderSelect({ files: collectedFiles }, currentPath.join("/"));
                  return;
                }
              
                try {
                  if (currentPath.length >= 1)
                    filteredData = Object.fromEntries(
                      Object.entries(filteredData).map(([aid, val]) => {
                        const obj = val as Record<string, any>;
                        return [aid, { [currentPath[0]]: obj[currentPath[0]] }];
                      })
                    );
              
                  if (currentPath.length >= 2)
                    filteredData = Object.fromEntries(
                      Object.entries(filteredData).map(([aid, val]) => {
                        const obj = val as Record<string, any>;
                        return [
                          aid,
                          {
                            [currentPath[0]]: {
                              [currentPath[1]]: obj[currentPath[0]]?.[currentPath[1]],
                            },
                          },
                        ];
                      })
                    );
              
                  if (currentPath.length >= 3)
                    filteredData = Object.fromEntries(
                      Object.entries(filteredData).map(([aid, val]) => {
                        const obj = val as Record<string, any>;
                        return [
                          aid,
                          {
                            [currentPath[0]]: {
                              [currentPath[1]]: {
                                [currentPath[2]]: obj[currentPath[0]]?.[currentPath[1]]?.[currentPath[2]],
                              },
                            },
                          },
                        ];
                      })
                    );
                } catch (err) {
                  console.error("Filtering error:", err);
                }
              
                onFolderSelect(filteredData, currentPath.join("/"));
              }}
              
          >
            <i
              className={`fas ${
                isLeaf
                  ? "fa-file text-gray-500"
                  : isOpen
                  ? "fa-folder-open text-violet-500"
                  : "fa-folder text-violet-500"
              }`}
            ></i>

            <span className="text-sm">{node.name}</span>
          </div>

          {node.children && isOpen && (
            <div className="pl-4 border-l border-gray-200 ml-2">
              {renderTree(node.children, currentPath)}
            </div>
          )}
        </div>
      );
    });

  return (
    <div className="bg-white border border-gray-300 rounded-lg shadow-md p-3 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-700 font-poppins">Folders</h2>
        {selectedKey && (
          <button
            title="Reset to All Top Files View"
            onClick={() => {
              onFolderSelect(apiData, "Top Files");
              setSelectedKey(null);
              setExpandedNodes({});
            }}
            className="p-2 rounded-full bg-violet-100 hover:bg-violet-200 text-violet-600 transition-all duration-200 shadow-sm"
          >
            <RotateCcw size={18} />
          </button>
        )}
      </div>

      {renderTree(treeData)}
    </div>
  );
};

// ------------------ MAIN FILE CENTER PAGE ------------------
const FileCenterPage: React.FC = () => {
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [allFiles, setAllFiles] = useState<FileDetails[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<FileDetails[]>([]);
  const [apiData, setApiData] = useState<any>(null);
  const [assetMap, setAssetMap] = useState<Record<string, string>>({});
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [isUploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { selectedAssetNameContext } = useAssetContext();
  const { setStatusMessage, setIsFileListLoading } = useFileCenterContext();
  const [isAllSelected, setIsAllSelected] = useState(false);
   const [loading, setLoading] = useState(true);
    const [gridVisibleRows, setGridVisibleRows] = useState<FileDetails[]>([]);
    const prevAssetFilterRef = useRef<string | null>(null);
const [folderFilterResetKey, setFolderFilterResetKey] = useState(0);

    // const folderOptions = useMemo(() => {
    //   const source = (gridVisibleRows && gridVisibleRows.length > 0) ? gridVisibleRows : filteredFiles || [];
    //   return Array.from(new Set(source.map((f) => f.folderName).filter(Boolean)));
    // }, [gridVisibleRows, filteredFiles]);
    
    const contextFiles = useMemo<FileDetails[]>(() => {
     
      if (!selectedAssetId) return filteredFiles;
    
      return filteredFiles.filter(
        f => f.assetId === selectedAssetId
      );
    }, [filteredFiles, selectedAssetId]);
    
    const folderOptions = useMemo(() => {
      return Array.from(
        new Set(
          contextFiles
            .map(f => f.folderName)
            .filter((v): v is string => Boolean(v))
        )
      );
    }, [contextFiles]);
    
    
  
  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => { },
  });
  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => { },
    });
  };
  // const gridApiRef = React.useRef<any>(null);
  const gridRef = React.useRef<any>(null);


  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const headers = await getHeaders();
        const res = await apiClient.get(
          `/assets/?page=1&page_size=500`,
          { headers }
        );
        const fetchedAssets = res.data.response.data;
        const map: Record<string, string> = {};
        fetchedAssets.forEach((asset: any) => {
          map[asset.asset_id] = asset.asset_name;
        });
        setAssetMap(map);
      } catch (err) {
        console.error("Failed to fetch assets", err);
      }
    };
    fetchAssets();
  }, []);
  const CustomHeader = (props: any) => {
    const { displayName, icon } = props;
    return (
      <div className="flex items-center gap-2 font-poppins font-bold text-gray-700 text-base">
        {icon}
        <span>{displayName}</span>
      </div>
    );
  };


  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    resizable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    suppressHeaderMenuButton: true,
    suppressHeaderContextMenu: true,
    cellStyle: {
      padding: '8px 12px',
      fontSize: '15px',
      fontFamily: "'Poppins', sans-serif",
      color: '#374151',
    },
  }), []);

  // const columnDefs = useMemo<ColDef<FileDetails>[]>(() => [
    const columnDefs = useMemo<ColDef<FileDetails>[]>(() => {
      // const folderOptions = Array.from(
      //   new Set(filteredFiles.map((f) => f.folderName).filter(Boolean))
      // );
          // console.log("[Folder Name Options]:", folderOptions);
      return [
        {
          headerName: '',
          field: 'selected',
          width: 70,
          sortable: false,
          filter: 'agTextColumnFilter',
          floatingFilter: true,
          headerComponent: () => (
             <div
             className="flex justify-center items-center h-full"
             style={{ marginLeft: '8px' }}
           >
             <FaDownload className="text-gray-600" />
           </div>
          ),
          cellRenderer: (params: any) => {
            const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              e.stopPropagation();
              const checked = e.target.checked;
              params.context.updateSelected(
                params.data.assetId,
                params.data.fileName,
                params.data.folderName,
                checked
              );
            };
        
            return (
              <div className="flex justify-center items-center h-full">
                <input
                  type="checkbox"
                  checked={!!params.data.selected}
                  onChange={handleChange}
                  onClick={(e) => e.stopPropagation()}
                  style={{ cursor: 'pointer', transform: 'scale(1.1)' }}
                />
              </div>
            );
          },
          floatingFilterComponent: (props: any) => {
            const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
              const checked = e.target.checked;
              props.context.toggleSelectAll(checked);
            };
        
            return (
              <div className="flex justify-center items-center h-full">
                <input
                  type="checkbox"
                  checked={props.context.isAllSelected}
                  onChange={handleSelectAll}
                  style={{
                    cursor: 'pointer',
                    transform: 'scale(1.2)',
                    marginLeft: '12px',
                  }}
                  title="Select All"
                />
              </div>
            );
          },
        },
  
    {
      headerName: "Asset",
      field: "assetId",
      flex: 1,
      minWidth: 160,
      headerComponent: SortHeader, 
      valueGetter: (params) =>
        assetMap?.[params.data?.assetId as keyof typeof assetMap] ??
        params.data?.assetId ??
        "—",
       
  // filterValueGetter: (params) =>
  // assetMap?.[params.data?.assetId as keyof typeof assetMap] ?? params.data?.assetId ?? "—",
      headerComponentParams: {
        displayName: "Asset",
        icon: <FaUser className="text-gray-600" />,
      },
      headerClass: "bg-gray-200 p-3 border-b border-gray-300",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      floatingFilterComponent: DropdownFloatingFilter,
      floatingFilterComponentParams: {
  options: Object.values(assetMap),
  placeholder: "Select Asset",
  onValueChange: (assetName: string | null) => {
    const assetId = assetName ? assetNameToId[assetName] : null;
    setSelectedAssetId(assetId);
    setFolderFilterResetKey(k => k + 1);
  }
  }
    },
    
    {
      headerName: "Folder Name",
      field: "folderName",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => params.data?.folderName ?? "—",
      headerComponent: SortHeader,
      headerComponentParams: {
        displayName: "Folder Name",
        icon: <FaBuilding className="text-gray-600" />,
      },
      headerClass: "bg-gray-200 p-3 border-b border-gray-300",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      floatingFilterComponent: DropdownFloatingFilter,
      floatingFilterComponentParams: {
        options: folderOptions,
        placeholder: "Select Folder",
        resetKey: folderFilterResetKey
      },
    },   
    {
      headerName: "As of Date",
      field: "createdDate",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      floatingFilterComponent: DateFloatingFilter,
      flex: 1,
      headerComponent: SortHeader,
      minWidth: 160,
      headerComponentParams: {
        displayName: "As of Date",
        icon: <FaCalendarAlt className="text-gray-600" />,
      },
      headerClass: "bg-gray-200 p-3 border-b border-gray-300",
    },
    {
      headerName: "File Name",
      field: "fileName",
      flex: 1,
      minWidth: 195,
      valueGetter: (params) => {
        const name = params.data?.fileName || "";
        return name.includes(".") ? name.substring(0, name.lastIndexOf(".")) : name;
      },
      headerComponent: SortHeader,
      headerComponentParams: {
        displayName: "File Name",
        icon: <FaFileAlt className="text-gray-600" />,
      },
      headerClass: "bg-gray-200 p-3 border-b border-gray-300",
    },
    {
      headerName: "File Type",
      field: "fileType",
      flex: 1,
      minWidth: 130,
      headerComponent: SortHeader,
      headerComponentParams: {
        displayName: "File Type",
        icon: <FaFileAlt className="text-gray-600" />,
      },
      headerClass: "bg-gray-200 p-3 border-b border-gray-300",
    },
    {
      headerName: "Created Date",
      field: "lastModified",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      floatingFilterComponent: DateFloatingFilter,
      flex: 1,
      headerComponent: SortHeader, 
      minWidth: 160,
      valueGetter: (params) => {
        const rawDate = params.data?.lastModified;
        if (!rawDate) return "—";
        const dateOnly = new Date(rawDate).toISOString().split("T")[0]; // ✅ Extract only date (YYYY-MM-DD)
        return dateOnly;
      },
      headerComponentParams: {
        displayName: "Created Date",
        icon: <FaCalendarAlt className="text-gray-600" />,
      },
      headerClass: "bg-gray-200 p-3 border-b border-gray-300",
    },
    
    
  // ], [assetMap]);
];
}, [assetMap, filteredFiles,folderOptions]);

  const addFloatingFilterPlaceholders = () => {
    document.querySelectorAll(".ag-floating-filter-input input").forEach((input) => {
      if (!input.getAttribute("placeholder")) {
        input.setAttribute("placeholder", "Search here...");
        // input.classList.add("floating-filter-input");
        input.classList.add("floating-filter-input"); 
      }
    });
  };
  const fetchFiles = async () => {
    setLoading(true);
    try {
      setIsFileListLoading(true);
      const headers = await getHeaders();


      const allowedAssetIds = Object.keys(assetMap);
      // console.log("Allowed Asset IDs:", allowedAssetIds);

      if (!allowedAssetIds.length) {
        setStatusMessage("No accessible assets found");
        return;
      }

      // console.log("Calling API: /file-center/list/all with payload", { asset_id_list: allowedAssetIds });
      const res = await apiClient.post(
        `file-center/list/all`,
        { asset_id_list: allowedAssetIds },
        { headers }
      );

      // console.log("API Response:", res.data);

      const data = res.data;
      if (data?.response?.status === "success") {
        const apiData = data.response.data;
        if (!apiData || Object.keys(apiData).length === 0) {
          setStatusMessage("No data available");
          setTreeData([]);
          setFilteredFiles([]);
          return;
        }

        setStatusMessage("");
        setApiData(apiData);

        // const transformed = transformToTree(apiData, setAllFiles, setFilteredFiles);
        // setTreeData(transformed);
        

        // const topFiles = extractTopFiles(apiData).map(f => ({ ...f, selected: false }));
        // setFilteredFiles(topFiles);
        const topFiles = extractTopFiles(apiData).map(f => ({ ...f, selected: false }));
        setFilteredFiles(topFiles);
        setGridVisibleRows([]);


      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error: any) {
      const apiError = error?.response?.data?.error;
      let message = "No data available";
      if (error?.response?.status === 403) {
        message = "You need permission to access this site";
      } else if (apiError?.error?.message) {
        message = apiError.error.message;
      } else if (apiError?.message) {
        message = apiError.message;
      } else if (error?.message && !error.message.includes("status code")) {
        message = error.message;
      }

      message = message
        .replace(/^(\d+):\s*\1\s*/, "")
        .replace(/^:\s*/, "")
        .trim();

      setStatusMessage(message || "No data available");
      setTreeData([]);
      setFilteredFiles([]);
      console.error("Fetch Files Error:", error);
    } finally {
      setLoading(false);
      setIsFileListLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(assetMap).length > 0) {
      fetchFiles();
    }
  }, [assetMap]);
  const assetNameToId = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(assetMap).forEach(([id, name]) => {
      map[name] = id;
    });
    return map;
  }, [assetMap]);
  
  useEffect(() => {
    if (!apiData) return;
  
    const tree = transformToTree(apiData, selectedAssetId);
    setTreeData(tree);
  
    // console.log(
    //   " Tree rebuilt for asset:",
    //   selectedAssetId ?? "ALL",
    //   tree.map(n => n.name)
    // );
  }, [apiData, selectedAssetId]);
  useEffect(() => {
    if (!apiData) return;
  
    setSelectedPath(null);
  
   
    const topFiles = extractTopFiles(apiData)
      .filter(f => !selectedAssetId || f.assetId === selectedAssetId)
      .map(f => ({ ...f, selected: false }));
  
    setFilteredFiles(topFiles);
    setGridVisibleRows([]);
  
  }, [selectedAssetId, apiData]);
  
  const getAssetsForSelectedFolder = (files: FileDetails[]) => {
    const uniqueAssets: Record<string, string> = {};
    files.forEach((f) => {
      uniqueAssets[f.assetId] = assetMap[f.assetId];
    });
    return Object.entries(uniqueAssets).map(([id, name]) => ({ assetId: id, assetName: name }));
  };

  const handleDownloadSelected = async () => {
    const selectedFiles = filteredFiles.filter(f => f.selected);
    if (!selectedFiles.length) {
      setDialog({
        open: true,
        title: "Warning",
        message: "No files selected for download!",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return;
    }

    let downloadedFiles: string[] = [];
    let failedFiles: string[] = [];

    for (const file of selectedFiles) {
      try {
        const headers = await getHeaders();

        const [year, month, day] = file.createdDate.split("-");
        const categoryName = (file as any).categoryName || "da-awards";

        const apiUrl = `/file-center/download/${file.assetId}/${categoryName}/${year}/${month}/${day}/${encodeURIComponent(file.fileName)}`;

        const response = await apiClient.get(apiUrl, { headers });

        const blobUrl = response.data?.response?.data?.[0]?.blob_url;
        if (!blobUrl) throw new Error("Blob URL missing in response");

        const blobResponse = await fetch(blobUrl);
        const blob = await blobResponse.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        downloadedFiles.push(file.fileName);
      } catch (error) {
        console.error(" Download failed for", file.fileName, error);
        failedFiles.push(file.fileName);
      }
    }


    if (downloadedFiles.length > 0 && failedFiles.length === 0) {
      // alert(` Downloaded: ${downloadedFiles.join(", ")}`);
      setDialog({
        open: true,
        title: "Success",
        message: `Downloaded file:\n${downloadedFiles.join(", ")}`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } else if (downloadedFiles.length > 0 && failedFiles.length > 0) {
      // alert(`Partial Success\nDownloaded: ${downloadedFiles.join(", ")}\nFailed: ${failedFiles.join(", ")}`);
      setDialog({
        open: true,
        title: "Partial Success",
        message: `Downloaded: ${downloadedFiles.join(
          ", "
        )}\nFailed: ${failedFiles.join(", ")}`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } else {
      // alert(` Failed to download: ${failedFiles.join(", ")}`);
      setDialog({
        open: true,
        title: "Failed",
        message: `Failed to download file(s):\n${failedFiles.join(", ")}`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    }
setFilteredFiles(prev => prev.map(file => ({ ...file, selected: false })));
setIsAllSelected(false);

  };

  const handleUpload = async (
    file: File,
    assetId: string,
    category: string,
    year: string,
    month: string,
    date: string
  ) => {
    if (!file)
      return;
    if (!assetId || !category || !year || !month || !date) {
      // alert("Please select all fields before uploading.");
      setDialog({
        open: true,
        title: "Warning",
        message:
          "Please select an asset and a valid date-based folder before uploading.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return;
    }

    const apiUrl = `/file-center/upload/${assetId}/${category}/${year}/${month}/${date}/${encodeURIComponent(file.name)}`;

    try {
      const headers = await getHeaders();
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post(apiUrl, formData, { headers });

      const blobUrl = response.data.response?.data?.[0]?.blob_url;
      if (!blobUrl) {
        // alert("Failed to retrieve Blob URL from API.");
        setDialog({
          open: true,
          title: "Warning",
          message: "Failed to retrieve Blob URL from API.",
          confirmButtonText: "Close",
          onConfirm: closeDialog,
        });
        return;
      }

      const putResponse = await fetch(blobUrl, {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob" },
        body: file,
      });

      if (!putResponse.ok) {
        const text = await putResponse.text();
        console.error("Blob upload failed:", putResponse.status, text);
        // alert("File upload to Blob Storage failed.");
        setDialog({
          open: true,
          title: "Failed",
          message: "File upload to Blob Storage failed.",
          confirmButtonText: "Close",
          onConfirm: closeDialog,
        });
        return;
      }

      // alert(" File uploaded successfully!");
      setDialog({
        open: true,
        title: "Success",
        message: "File uploaded successfully",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      fetchFiles();
    } catch (error) {
      console.error("Upload error:", error);
      // alert("An error occurred while uploading the file.");
      setDialog({
        open: true,
        title: "Failed",
        message: "An error occurred while uploading the file.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    }
  };


  // const handleDateSelect = (files: FileDetails[]) => setFilteredFiles(files);
  const handleDateSelect = (files: FileDetails[]) => {
    setFilteredFiles(files);
    setGridVisibleRows([]);
  };
  
  const gridApiRef = React.useRef<any>(null);


  return (
    <div>
      <div className="flex justify-between items-center mx-10 mt-10 font-poppins">
        <h1 className="text-2xl font-bold text-gray-700 mb-4">File Center</h1>
      </div>
      <div className="flex p-4 bg-[#EAE7FF] gap-4 max-h-full min-h-[800px] ml-10 mr-10 mb-4 rounded-lg flex-col md:flex-row-reverse">

        <div className="w-full flex flex-col md:w-1/4 bg-white border border-gray-300 rounded-lg shadow-md p-4 font-poppins">
          <div className="bg-gray-100 text-gray-700 text-sm font-semibold px-4 py-2 rounded mb-4">
            <div className="relative">
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search folders"
                className="w-full h-10 pl-10 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
              />
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <CategoryTree
             key={selectedAssetId ?? "ALL"}
              treeData={
                searchTerm.trim()
                  ? treeData
                    .map((node) => filterTree(node, searchTerm.trim().toLowerCase()))
                    .filter(Boolean) as TreeNode[]
                  : treeData
              }
              onDateSelect={handleDateSelect}
              apiData={apiData}
              selectedAssetId={selectedAssetId}
              onFolderSelect={(filteredData, context) => {
                setSelectedPath(context);
                const pathParts = context.split("/");
                const hasFiles = !!filteredData.files;
                if (hasFiles) {
                  setFilteredFiles(filteredData.files.map((f: any) => ({ ...f, selected: false })));
                } else {
                  const topFiles = extractTopFiles(filteredData).map((f) => ({ ...f, selected: false }));
                  setFilteredFiles(topFiles);
                }
                setGridVisibleRows([]);
              }}
                          
            />

          </div>
        </div>

        <div className="flex-1 bg-white border border-gray-300 rounded-lg shadow-md font-poppins p-4 flex flex-col">

          <div className="flex justify-between items-center mb-4 w-full bg-gray-100 rounded-md p-2 px-4">

            <div className="flex flex-col text-gray-700 font-semibold font-poppins">
              {selectedPath ? (
                <div>
                  <span className="text-sm text-gray-500">Showing files for:</span>{" "}
                  <span className="text-base text-gray-700 ml-1">
                    {selectedPath.replace(/\//g, " / ")}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-gray-500">Showing: Top Files</span>
              )}
            </div>


            <div className="flex gap-3 items-center">
              <UploadButton onClick={() => setUploadDialogOpen(true)} />


              <DownloadButton onClick={handleDownloadSelected} />
            </div>
          </div>

          <div className="mb-4 bg-gray-100 text-center py-2 px-4 text-gray-600 font-semibold rounded-md font-poppins">
  <h2 className="text-xl font-semibold text-gray-600">
    {(() => {
      if (!selectedPath || selectedPath.toLowerCase().includes("top files")) {
        return "Top Files";
      }

      const parts = selectedPath.split("/");
      const formattedPath = parts.join(" / ");
      const depth = parts.length;

      if (depth === 4) {
        return (
          <>
            Files for :{" "}
            <span className="text-gray-800 font-bold">{formattedPath}</span>
          </>
        );
      } else if (depth === 3) {
        return (
          <>
            All Files for :{" "}
            <span className="text-gray-800 font-bold">{formattedPath}</span>
          </>
        );
      } else {
        return (
          <>
            Top Files for :{" "}
            <span className="text-gray-800 font-bold">{formattedPath}</span>
          </>
        );
      }
    })()}
  </h2>
</div>


          <div className="ag-theme-alpine font-poppins text-gray-700" style={{ height: "570px", maxHeight: "600px", width: "100%", overflow: "auto", border: '1px solid #E7E4FF', borderRadius: '8px' }}>
          {loading && (
    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
      <LoadingStyle />
    </div>
  )}
            <AgGridReact
              theme="legacy"
              // ref={gridRef}
              rowData={filteredFiles}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowHeight={60}
              headerHeight={55}
              pagination
              className="ag-theme-alpine"
              paginationPageSize={50}
                      rowSelection={{ mode: 'multiRow', enableClickSelection: false }}
              getRowId={params => `${params.data.fileName}_${params.data.assetId}_${params.data.folderName ?? ''}`}
              onGridReady={(params) => {
                gridApiRef.current = params.api;
              }}
              isExternalFilterPresent={() => !!selectedAssetId}
              doesExternalFilterPass={(node) => {
                if (!selectedAssetId) return true;
                return node.data?.assetId === selectedAssetId;
              }}
              
              onFilterChanged={(params) => {
                const visibleRows: FileDetails[] = [];
                params.api.forEachNodeAfterFilter((node: any) => {
                  if (node.data) visibleRows.push(node.data);
                });
              
                setGridVisibleRows(prev => {
                  if (prev.length === visibleRows.length) {
                    let same = true;
                    for (let i = 0; i < prev.length; i++) {
                      if (
                        prev[i].assetId !== visibleRows[i].assetId ||
                        prev[i].fileName !== visibleRows[i].fileName ||
                        prev[i].folderName !== visibleRows[i].folderName
                      ) {
                        same = false;
                        break;
                      }
                    }
                    if (same) return prev; 
                  }
              
                  return visibleRows;
                });
              }}
              suppressRowTransform={true}  
              context={{
                updateSelected: (
                  assetId: string,
                  fileName: string,
                  folderName: string,
                  selected: boolean
                ) => {
                  // console.log(
                  //   " Updating selection for file:",
                  //   { assetId, fileName, folderName, selected }
                  // );
                  setFilteredFiles((prev) =>
                    prev.map((file) =>
                      file.assetId === assetId &&
                      file.fileName === fileName &&
                      file.folderName === folderName
                        ? { ...file, selected }
                        : file
                    )
                  );
                },
              
                toggleSelectAll: (selectAll: boolean) => {
              
                  if (!gridApiRef.current) {
                    return;
                  }
              
                  try {
                    const visibleRows: any[] = [];
                    gridApiRef.current.forEachNodeAfterFilterAndSort((node: any) => {
                      if (node.data) visibleRows.push(node.data);
                    });
              
                    setFilteredFiles((prev) =>
                      prev.map((file) => {
                        const isVisible = visibleRows.some(
                          (row) =>
                            row.assetId === file.assetId &&
                            row.fileName === file.fileName &&
                            row.folderName === file.folderName
                        );
                        return isVisible ? { ...file, selected: selectAll } : file;
                      })
                    );
              
                    setIsAllSelected(selectAll);
                    console.log(" Updated all visible rows to selected =", selectAll);
                  } catch (err) {
                    console.error(" Error in toggleSelectAll:", err);
                  }
                },
              
                isAllSelected,
              }}
              
              
              onFirstDataRendered={addFloatingFilterPlaceholders}
            />
          </div>
        </div>
        {/* </div> */}
      </div>
      {isUploadDialogOpen && (
        <UploadDialog
          isOpen={isUploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          onUpload={handleUpload}
          assets={Object.entries(assetMap).map(([id, name]) => ({
            assetId: id,
            assetName: name,
          }))}
          apiData={apiData}
        />
      )}
      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          onConfirm={dialog.onConfirm}
          onClose={closeDialog}
        />
      )}

    </div>
  );
};

export default FileCenterPage;
